import { useEffect, useRef, useCallback } from 'react';
import { POSService } from '../services/pos/posService';

interface UseAutoSyncOptions {
  mesaId: string;
  posService: POSService | null;
  enabled?: boolean;
  intervalSeconds?: number;
  onSyncStart?: () => void;
  onSyncComplete?: (success: boolean) => void;
  onSyncError?: (error: string) => void;
}

/**
 * Hook to automatically sync a mesa with POS at regular intervals
 */
export function useAutoSync({
  mesaId,
  posService,
  enabled = true,
  intervalSeconds = 30,
  onSyncStart,
  onSyncComplete,
  onSyncError
}: UseAutoSyncOptions) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isSyncingRef = useRef(false);

  const syncMesa = useCallback(async () => {
    if (!posService || !mesaId || isSyncingRef.current) {
      return;
    }

    isSyncingRef.current = true;
    onSyncStart?.();

    try {
      console.log(`🔄 Auto-syncing mesa ${mesaId}...`);
      const result = await posService.syncMesa(mesaId);
      
      if (result.success) {
        console.log(`✅ Auto-sync successful for mesa ${mesaId}`);
        onSyncComplete?.(true);
      } else {
        console.warn(`⚠️ Auto-sync failed for mesa ${mesaId}:`, result.error);
        onSyncError?.(result.error || 'Sync failed');
        onSyncComplete?.(false);
      }
    } catch (error) {
      console.error(`❌ Auto-sync error for mesa ${mesaId}:`, error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      onSyncError?.(errorMessage);
      onSyncComplete?.(false);
    } finally {
      isSyncingRef.current = false;
    }
  }, [mesaId, posService, onSyncStart, onSyncComplete, onSyncError]);

  useEffect(() => {
    if (!enabled || !posService || !mesaId) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial sync on mount
    syncMesa();

    // Set up interval for periodic syncing
    intervalRef.current = setInterval(syncMesa, intervalSeconds * 1000);

    console.log(`🔁 Auto-sync enabled for mesa ${mesaId} (every ${intervalSeconds}s)`);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log(`🛑 Auto-sync stopped for mesa ${mesaId}`);
      }
    };
  }, [enabled, posService, mesaId, intervalSeconds, syncMesa]);

  // Return manual sync function
  return { syncNow: syncMesa };
}
